import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  Rating,
  Divider,
  Paper,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import CachedIcon from '@mui/icons-material/Cached';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import ProductCard from './common/ProductCard';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getProductById, getAllProducts } from '../services/api';
import { formatPrice, calculateDiscount } from '../utils/formatPrice';

const Viewproduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist, isInCart } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  const inWishlist = product ? isInWishlist(product._id) : false;
  const inCart = product ? isInCart(product._id) : false;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productRes, allProductsRes] = await Promise.all([
          getProductById(id),
          getAllProducts(),
        ]);

        const productData = productRes.data.data;
        setProduct(productData);

        const allProducts = allProductsRes.data.data || [];
        const categoryName = productData.ProductCategory?.CategoryName || productData.ProductCategory;
        const related = allProducts
          .filter((p) => {
            const pCat = p.ProductCategory?.CategoryName || p.ProductCategory;
            return pCat === categoryName && p._id !== productData._id;
          })
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setQuantity(1);
  }, [id]);

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    addToCart(product, quantity);
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    addToCart(product, quantity);
    navigate('/cart');
  };

  const handleWishlistToggle = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    if (inWishlist) {
      removeFromWishlist(product._id);
    } else {
      addToWishlist(product);
    }
  };

  if (loading) return <Loader message="Loading product..." />;

  if (!product) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
        <Navbar />
        <Container sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5">Product not found</Typography>
          <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>Go Home</Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  const price = Number(product.ProductPrice) || 0;
  const mrp = Number(product.ProductMRP) || price;
  const discount = calculateDiscount(mrp, price);
  const categoryName = product.ProductCategory?.CategoryName || product.ProductCategory;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb
          items={[
            { label: 'Shop', path: '/filter/All' },
            { label: categoryName, path: `/filter/${categoryName}` },
            { label: product.ProductName },
          ]}
        />

        <Grid container spacing={4}>
          {/* Image Section */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, bgcolor: '#fff', border: '1px solid #eee', position: 'relative' }}>
              {discount > 0 && (
                <Chip label={`${discount}% OFF`} sx={{ position: 'absolute', top: 16, left: 16, bgcolor: '#d32f2f', color: '#fff', fontWeight: 'bold' }} />
              )}
              <IconButton
                onClick={handleWishlistToggle}
                sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                {inWishlist ? <FavoriteIcon sx={{ color: '#e53935' }} /> : <FavoriteBorderIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <img
                  src={product.ProductMainImgUrl}
                  alt={product.ProductName}
                  style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} md={7}>
            <Box sx={{ bgcolor: '#fff', p: 4, borderRadius: 4, border: '1px solid #eee' }}>
              <Typography variant="h4" fontWeight="800" color="#2D1B14" gutterBottom>
                {product.ProductName}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={4.5} precision={0.5} readOnly size="small" />
                <Typography variant="body2" color="text.secondary" fontWeight="500">4.5 (128 reviews)</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {/* Fixed: Price color changed to Green tone */}
                <Typography variant="h3" fontWeight="900" color="#2e7d32">
                  Rs.{formatPrice(price)}
                </Typography>
                {mrp > price && (
                  <Typography variant="h5" sx={{ textDecoration: 'line-through', color: '#999', fontWeight: '500' }}>
                    Rs.{formatPrice(mrp)}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                {product.ProductShortDesc}
              </Typography>

              {/* Quantity Selector */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#555' }}>QUANTITY</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: '2px solid #f0f0f0', borderRadius: '12px', bgcolor: '#fafafa' }}>
                    <IconButton onClick={() => setQuantity(Math.max(1, quantity - 1))} sx={{ color: '#876514' }}>
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ px: 2, fontWeight: 'bold', minWidth: 40, textAlign: 'center', color: '#2D1B14' }}>
                      {quantity}
                    </Typography>
                    <IconButton onClick={() => setQuantity(quantity + 1)} sx={{ color: '#876514' }}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAddToCart}
                  sx={{
                    py: 1.8,
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    bgcolor: inCart ? '#4caf50' : '#FFD700', 
                    color: '#000',
                    boxShadow: '0 4px 14px 0 rgba(255,215,0,0.39)',
                    '&:hover': {
                      bgcolor: inCart ? '#388e3c' : '#FFC400',
                    }
                  }}
                >
                  {inCart ? 'IN CART' : 'ADD TO CART'}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleBuyNow}
                  sx={{
                    py: 1.8,
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    bgcolor: '#2e7d32', // Fixed: Buy Now in Green
                    color: '#fff',
                    '&:hover': { bgcolor: '#1b5e20' }
                  }}
                >
                  BUY NOW
                </Button>
              </Box>
            </Box>

            {/* Features Row */}
            <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-around', bgcolor: '#fff', border: '1px solid #eee' }}>
               <Box sx={{ textAlign: 'center' }}>
                  <LocalShippingIcon sx={{ color: '#876514' }} />
                  <Typography variant="caption" display="block" fontWeight="bold">FREE SHIPPING</Typography>
               </Box>
               <Box sx={{ textAlign: 'center' }}>
                  <VerifiedIcon sx={{ color: '#876514' }} />
                  <Typography variant="caption" display="block" fontWeight="bold">100% PURE</Typography>
               </Box>
               <Box sx={{ textAlign: 'center' }}>
                  <CachedIcon sx={{ color: '#876514' }} />
                  <Typography variant="caption" display="block" fontWeight="bold">EASY RETURN</Typography>
               </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Related Products Section - Fixed: Using relatedProducts here to remove warning */}
        {relatedProducts.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="800" color="#2D1B14" sx={{ mb: 3 }}>
              Related Products
            </Typography>
            <Grid container spacing={3}>
              {relatedProducts.map((relProduct) => (
                <Grid item xs={12} sm={6} md={3} key={relProduct._id}>
                  <ProductCard product={relProduct} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default Viewproduct;